import React from 'react';
import { Platform } from '../types';
import { GeneratedSocialPostResult } from 'src/api/types/social-types';

export function platformTheme(p: Platform | string) {
  const map: Record<
    string,
    { brand: string; maxHashtags: number; showLink: boolean }
  > = {
    email: { brand: '#2563EB', maxHashtags: 0, showLink: true },
    facebook: { brand: '#1877F2', maxHashtags: 8, showLink: true },
    instagram: { brand: '#E1306C', maxHashtags: 8, showLink: false },
    x: { brand: '#000000', maxHashtags: 2, showLink: true },
    linkedin: { brand: '#0A66C2', maxHashtags: 5, showLink: true },
    tiktok: { brand: '#000000', maxHashtags: 8, showLink: false },
    youtube: { brand: '#FF0000', maxHashtags: 5, showLink: true },
    pinterest: { brand: '#E60023', maxHashtags: 8, showLink: true },
    threads: { brand: '#000000', maxHashtags: 5, showLink: true },
    whatsapp: { brand: '#25D366', maxHashtags: 0, showLink: true },
    reddit: { brand: '#FF4500', maxHashtags: 2, showLink: true },
  };
  return map[p] ?? { brand: '#444', maxHashtags: 5, showLink: true };
}

export function linkify(text: string) {
  const urlRe = /(https?:\/\/[^\s)]+)(?![^<]*>)/g;
  return text.split(urlRe).map((part, i) =>
    urlRe.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noreferrer">
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

export function truncateForPlatform(body: string, platform: Platform | string) {
  // Light preview truncation for platforms that truncate aggressively
  const limits: Record<string, number> = {
    x: 280,
    threads: 500,
    linkedin: 3000,
    facebook: 5000,
    instagram: 2200,
  };
  const max = limits[platform] ?? 2200;
  return body.length > max ? body.slice(0, max - 1) + '…' : body;
}

import { CircularProgress, Box, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import FacebookPostPreview from '../previews/facebook';
import InstagramPostPreview from '../previews/instagram';
import LinkedInPostPreview from '../previews/linkedin';
import XPostPreview from '../previews/x';
import TikTokPostPreview from '../previews/tiktok';

type Props = {
  result?: GeneratedSocialPostResult;
  loading?: boolean;
  error?: string;
};

export const PreviewPanel: React.FC<Props> = ({ result, loading, error }) => {
  if (loading) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight={200}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Generating preview…</Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight={200}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  if (!result || !result.post) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight={200}>
        <Typography color="text.secondary">
          No preview available yet.
        </Typography>
      </Box>
    );
  }

  const post = result.post;

  // Compose linkPreview from post.cta if available
  const linkPreview = post.cta?.url
    ? {
        url: post.cta.url,
        title: post.cta.label || post.cta.url,
        description: post.topic || '',
      }
    : undefined;

  if (post.platform === 'facebook') {
    return (
      <FacebookPostPreview
        post={result}
        profileName={post.location?.title}
        counters={{ likes: 10, comments: 20, shares: 30 }}
        profileAvatarUrl="https://upload.wikimedia.org/wikipedia/commons/2/2c/Default_pfp.svg"
        when="Just now"
        linkPreview={linkPreview}
      />
    );
  }

  if (post.platform === 'instagram') {
    // IG feed doesn’t show link previews; we still show a small note with CTA URL.
    return (
      <InstagramPostPreview
        data={result}
        profileName={post.location?.title}
        counters={{ likes: 10482, comments: 144, shares: 20 }}
        profileAvatarUrl="https://upload.wikimedia.org/wikipedia/commons/2/2c/Default_pfp.svg"
        when="Just now"
      />
    );
  }

  if (post.platform === 'linkedin') {
    return (
      <LinkedInPostPreview
        data={result}
        counters={{ reactions: 1247, comments: 98, reposts: 34 }}
        profileAvatarUrl="https://upload.wikimedia.org/wikipedia/commons/2/2c/Default_pfp.svg"
        profileName={post.location?.title}
        profileSubtitle={'Building AI Agent · Syntraxis'}
        when="1h"
        linkPreview={linkPreview}
      />
    );
  }

  if (post.platform === 'x' || post.platform === 'twitter') {
    return (
      <XPostPreview
        data={result}
        counts={{
          replies: 1234,
          reposts: 567,
          likes: 9876,
          bookmarks: 321,
          views: '1.2M',
        }}
        displayName={post.location?.title}
        handle={
          '@' +
          (post.location?.title || 'username').toLowerCase().replace(/\s+/g, '')
        }
        verified={true}
        when="2h"
        timeMeta="12:34 PM · Oct 20, 2025"
        avatarUrl="https://upload.wikimedia.org/wikipedia/commons/2/2c/Default_pfp.svg"
      />
    );
  }

  if (post.platform === 'tiktok') {
    return (
      <TikTokPostPreview
        data={result}
        counts={{
          views: '12.3K',
          likes: 1842,
          comments: 97,
          shares: 45,
          bookmarks: 12,
        }}
        profileName={post.location?.name}
        handle={
          '@' +
          (post.location?.name || 'username').toLowerCase().replace(/\s+/g, '')
        }
        when="Just now"
        avatarUrl="https://upload.wikimedia.org/wikipedia/commons/2/2c/Default_pfp.svg"
      />
    );
  }

  if (post.platform === 'email') {
    const sender = post.location?.title || 'Your business';
    const subjectLine =
      post.topic || post.cta?.label || `A note from ${sender}`;
    const ctaLabel = post.cta?.label || post.cta?.url;
    const ctaUrl = post.cta?.url;
    const theme = platformTheme('email');

    return (
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.brand}22`,
          borderRadius: 2,
          p: 2,
          bgcolor: '#fff',
        }}>
        <Typography variant="overline" color="text.secondary">
          Email preview
        </Typography>
        <Typography variant="subtitle1" fontWeight={700} mt={0.5}>
          {subjectLine}
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.25}>
          From: {sender}
        </Typography>
        <Typography
          variant="body1"
          sx={{ mt: 1.5, whiteSpace: 'pre-line', lineHeight: 1.6 }}>
          {post.body || 'Your generated email will appear here.'}
        </Typography>
        {ctaLabel && (
          <Box
            sx={{
              mt: 1.5,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              color: theme.brand,
              fontWeight: 600,
            }}>
            <span>{ctaLabel}</span>
            {ctaUrl && (
              <a
                href={ctaUrl}
                target="_blank"
                rel="noreferrer"
                style={{ color: theme.brand }}>
                {ctaUrl}
              </a>
            )}
          </Box>
        )}
      </Paper>
    );
  }

  const theme = platformTheme(post.platform);
  const body = truncateForPlatform(post.body || '', post.platform);

  const imgSrc = result.imageLinkUrl || post.image?.link_url || '';

  const showLink = theme.showLink && !!post.cta?.url;
  const hashtagRow = (post.hashtags || [])
    .slice(0, theme.maxHashtags)
    .join(' ');
  const mentionRow = (post.mentions || []).join(' ');

  const displayName = post.location?.title || 'Your brand';
  const handle =
    '@' +
    (post.location?.title || 'yourbrand')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '')
      .slice(0, 12);
  const when = 'Just now';

  return (
    <article
      style={{
        border: `1px solid ${theme.brand}22`,
        borderRadius: 14,
        padding: 16,
        maxWidth: 640,
        width: '100%',
        alignSelf: 'flex-start',
        background: '#fff',
        boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
        fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
      }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 10,
        }}>
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: `${theme.brand}15`,
            border: `1px solid ${theme.brand}35`,
            display: 'grid',
            placeItems: 'center',
            fontWeight: 700,
            color: theme.brand,
          }}>
          {displayName.slice(0, 1)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              color: '#111',
              fontWeight: 700,
            }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {displayName}
            </span>
            <span
              style={{
                background: `${theme.brand}15`,
                color: theme.brand,
                borderRadius: 999,
                padding: '2px 8px',
                fontSize: 11,
                textTransform: 'capitalize',
              }}>
              {post.platform || 'social'}
            </span>
          </div>
          <div style={{ color: '#6b7280', fontSize: 12, marginTop: 2 }}>
            {handle} · {when}
          </div>
        </div>
        <div style={{ color: '#9ca3af', fontSize: 18, fontWeight: 700 }}>…</div>
      </header>

      <section
        style={{
          color: '#111',
          lineHeight: 1.6,
          fontSize: 15,
          whiteSpace: 'pre-wrap',
          marginBottom: 8,
        }}>
        {linkify(body)}
      </section>

      {imgSrc && (
        <figure
          style={{ margin: '10px 0 0', borderRadius: 12, overflow: 'hidden' }}>
          <img
            src={imgSrc}
            alt={post.image?.alt || 'post image'}
            style={{ width: '100%', display: 'block' }}
          />
          {post.image?.alt && (
            <figcaption style={{ color: '#666', fontSize: 12, marginTop: 6 }}>
              {post.image.alt}
            </figcaption>
          )}
        </figure>
      )}

      {(mentionRow || hashtagRow || showLink || post.cta?.label) && (
        <footer style={{ marginTop: 12, display: 'grid', gap: 8 }}>
          {mentionRow && (
            <div style={{ color: '#4b5563', fontSize: 14 }}>{mentionRow}</div>
          )}
          {hashtagRow && (
            <div style={{ color: '#4b5563', fontSize: 14 }}>{hashtagRow}</div>
          )}
          {showLink && (
            <div
              style={{
                border: `1px solid ${theme.brand}22`,
                borderRadius: 10,
                padding: '10px 12px',
                background: '#f9fafb',
                display: 'inline-flex',
                width: 'fit-content',
              }}>
              <a
                href={post.cta.url}
                target="_blank"
                rel="noreferrer"
                style={{
                  color: theme.brand,
                  textDecoration: 'none',
                  fontWeight: 600,
                }}>
                {post.cta?.label || post.cta.url || 'Open link'}
              </a>
            </div>
          )}
        </footer>
      )}
    </article>
  );
};
